import { LockKeyIcon } from '@phosphor-icons/react'
import {
  Button,
  EmptyState,
  EmptyStateActions,
  EmptyStateDescription,
  EmptyStateIcon,
  EmptyStateTitle,
} from '@curri/components'
import { useState, type ReactNode } from 'react'

import './bookings.css'

import {
  PAYLOAD_READ_SCOPE,
  type BookingStatusFilter,
} from '../../api/bookings.ts'
import { useAuth } from '../../auth/AuthProvider.tsx'
import { GhostBar } from '../../ghost/GhostBar.tsx'
import { BarcodeStamp } from '../landing/Barcode.tsx'

import { BookingsCard } from './BookingsCard.tsx'
import { SignedOut } from './SignedOut.tsx'
import { useBookings } from './useBookingsData.ts'

// The bookings dashboard (INT-432): every order created through the API, from
// either the payloads store or a legacy API delivery, in one list. Answers the
// reconciliation questions that currently route through a Curri employee: what
// did my integration book, what state is it in, and what did Curri send my
// endpoint about it.

const Page = ({ children }: { children: ReactNode }) => (
  // The shell's pane is `overflow: hidden`, so the screen owns its own scroll.
  <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
    <div
      style={{
        // Wider than the webhooks page's 960: the seven-column table needs the
        // room, and the design's frame is 1240 with the same padding.
        maxWidth: 1100,
        margin: '0 auto',
        padding: '40px 32px 64px',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}
    >
      {children}
    </div>
  </div>
)

const PageHeading = () => (
  <div style={{ marginBottom: 8 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
      <h1 style={{ fontSize: 25 }}>Bookings</h1>
      <span style={{ flex: 1 }} />
      <BarcodeStamp tone="muted" />
    </div>
    <p
      style={{
        margin: '12px 0 0',
        fontSize: 15,
        lineHeight: 1.6,
        color: 'var(--curri-text-secondary)',
        maxWidth: '66ch',
        textWrap: 'pretty',
      }}
    >
      Every order created through the API. Open a row for the request that
      produced it, the webhooks we sent about it, and the identifiers you need
      to reconcile it on your side.
    </p>
  </div>
)

/**
 * Rendered when the session's token was issued without `payload:read` —
 * checked up front against the granted scopes rather than waiting for a 403,
 * for the same reason the webhooks screen does: an empty table with an error
 * banner reads as "my bookings are broken", which is the wrong diagnosis.
 */
const MissingScope = () => {
  const { signOut } = useAuth()

  return (
    <Page>
      <PageHeading />
      <EmptyState variant="warning">
        <EmptyStateIcon>
          <LockKeyIcon />
        </EmptyStateIcon>
        <EmptyStateTitle>This session cannot read bookings</EmptyStateTitle>
        <EmptyStateDescription>
          Your access token was issued without the <code>{PAYLOAD_READ_SCOPE}</code>{' '}
          scope. Signing in again will request it. If it still does not appear,
          the OAuth client this portal uses needs <code>{PAYLOAD_READ_SCOPE}</code>{' '}
          added to its registration — email engineering@curri.com.
        </EmptyStateDescription>
        <EmptyStateActions>
          <Button label="Sign out and try again" onClick={signOut} size="base" />
        </EmptyStateActions>
      </EmptyState>
    </Page>
  )
}

const Dashboard = () => {
  const [status, setStatus] = useState<BookingStatusFilter>('all')
  const bookings = useBookings({ status })

  return (
    <Page>
      <PageHeading />
      {/* Above the data, not beside it: whoever is reading this screen has to
          know whose data it is before they read any of it. Renders nothing for
          a partner session. */}
      <GhostBar />
      <BookingsCard
        isLoadingMore={bookings.isLoadingMore}
        loadMore={bookings.loadMore}
        loadMoreFailed={bookings.loadMoreFailed}
        nextCursor={bookings.nextCursor}
        onStatusChange={setStatus}
        resource={bookings.resource}
        status={status}
      />
    </Page>
  )
}

export const Bookings = ({
  onNavigate,
}: {
  onNavigate: (path: string) => void
}) => {
  const { state } = useAuth()

  // This screen owns its signed-out state rather than falling through to the
  // shell's generic placeholder: the pitch for the dashboard lives where the
  // dashboard would be.
  if (state.status !== 'authenticated') {
    return <SignedOut onNavigate={onNavigate} />
  }

  return state.caller.grantedScopes.includes(PAYLOAD_READ_SCOPE) ? (
    <Dashboard />
  ) : (
    <MissingScope />
  )
}
